// src/components/NewVideoComponentForm.js
import React, { useEffect, useMemo, useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage, auth } from '../firebaseConfig';

export default function NewVideoComponentForm({
                                                  userId,

                                                  // title/description
                                                  title = '',
                                                  description = '',
                                                  onTitleChange,
                                                  onDescriptionChange,
                                                  existingVideoUrl = '',
                                                  existingFeatureImageUrl = '',

                                                  // completion callbacks
                                                  onUploadComplete,          // (url, title, description, gate?, featureImageUrl?) => void
                                                  enablePublishButton = true,
                                                  onSubmitAndPublish,        // (url, title, description, gate?, featureImageUrl?) => void

                                                  onCancel,
                                                  error,
                                              }) {
    const [videoFile, setVideoFile]   = useState(null);
    const [imageFile, setImageFile]   = useState(null);
    const [uploading, setUploading]   = useState(false);
    const [localError, setLocalError] = useState('');
    const [removeFeatureImage, setRemoveFeatureImage] = useState(false);

    const MAX_VIDEO_BYTES = 100 * 1024 * 1024; // 100 MB

    const videoPreviewUrl = useMemo(
        () => (videoFile ? URL.createObjectURL(videoFile) : ''),
        [videoFile]
    );
    const imagePreviewUrl = useMemo(
        () => (imageFile ? URL.createObjectURL(imageFile) : ''),
        [imageFile]
    );
    const displayVideoUrl = videoPreviewUrl || existingVideoUrl;
    const displayImageUrl = removeFeatureImage ? '' : (imagePreviewUrl || existingFeatureImageUrl);

    useEffect(() => {
        setVideoFile(null);
        setImageFile(null);
        setLocalError('');
        setRemoveFeatureImage(false);
    }, [existingVideoUrl, existingFeatureImageUrl]);

    const handleVideoChange = (e) => {
        const f = e.target.files?.[0];
        setLocalError('');
        if (!f) return;
        if (f.size > MAX_VIDEO_BYTES) {
            const msg = `File is too large. Maximum size is ${MAX_VIDEO_BYTES / (1024 * 1024)} MB.`;
            alert(msg);
            setLocalError(msg);
            return;
        }
        setVideoFile(f);
    };

    const handleImageChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setImageFile(f);
        setRemoveFeatureImage(false);
    };

    // Upload helper that returns { videoUrl, featureImageUrl }
    const uploadAll = () => new Promise(async (resolve, reject) => {
        try {
            setLocalError('');
            if (!videoFile && !existingVideoUrl) {
                setLocalError('Please select a video file.');
                return reject(new Error('missing-video'));
            }
            if (videoFile && videoFile.size > MAX_VIDEO_BYTES) {
                const msg = `File is too large. Maximum size is ${MAX_VIDEO_BYTES / (1024 * 1024)} MB.`;
                alert(msg);
                setLocalError(msg);
                return reject(new Error('file-too-large'));
            }

            setUploading(true);
            const uid = userId || auth.currentUser?.uid || 'anonymous';
            const ts  = Date.now();

            // 1) upload video (optional when editing)
            let videoUrl = existingVideoUrl || '';
            if (videoFile) {
                const safeVideoName = videoFile.name.replace(/[^\w.\-]/g, '_');
                const videoRef = ref(storage, `videos/${uid}/${ts}_${safeVideoName}`);
                await new Promise((res, rej) =>
                    uploadBytesResumable(videoRef, videoFile).on('state_changed', null, rej, res)
                );
                videoUrl = await getDownloadURL(videoRef);
            }

            // 2) upload feature image (optional)
            let featureImageUrl = removeFeatureImage ? '' : (existingFeatureImageUrl || '');
            if (imageFile) {
                const safeImgName = imageFile.name.replace(/[^\w.\-]/g, '_');
                const imgRef = ref(storage, `videoFeatureImages/${uid}/${ts}_${safeImgName}`);
                await new Promise((res, rej) =>
                    uploadBytesResumable(imgRef, imageFile).on('state_changed', null, rej, res)
                );
                featureImageUrl = await getDownloadURL(imgRef);
            }

            setUploading(false);
            resolve({ videoUrl, featureImageUrl });
        } catch (e) {
            console.error(e);
            setUploading(false);
            setLocalError('Upload failed.');
            reject(e);
        }
    });

    const handleSave = async () => {
        try {
            const { videoUrl, featureImageUrl } = await uploadAll();
            onUploadComplete?.(videoUrl, title, description, undefined, featureImageUrl);
        } catch {/* localError set */}
    };

    const handleSaveAndPublish = async () => {
        try {
            const { videoUrl, featureImageUrl } = await uploadAll();
            onSubmitAndPublish?.(videoUrl, title, description, undefined, featureImageUrl);
        } catch {/* localError set */}
    };

    return (
        <div className="new-component-form video-form">
            <h1>Video Component Form</h1>

            <input
                type="text"
                placeholder="Video Title"
                value={title}
                onChange={e=>onTitleChange?.(e.target.value)}
                className="muulti-input"
            />
            <textarea
                placeholder="Video Description"
                value={description}
                onChange={e=>onDescriptionChange?.(e.target.value)}
                className="muulti-textarea"
            />

            <h3>Select a video file</h3>
            <input type="file" accept="video/*" onChange={handleVideoChange} className="muulti-input" />
            {!!displayVideoUrl && (
                <video src={displayVideoUrl} controls style={{ width: '100%', marginTop: 8, borderRadius: 8 }} />
            )}

            <h3 style={{ marginTop: 16 }}>Optional feature image (thumbnail)</h3>
            <input type="file" accept="image/*" onChange={handleImageChange} className="muulti-input" />
            {!!existingFeatureImageUrl && !imageFile && (
                <button
                    type="button"
                    onClick={() => setRemoveFeatureImage((v) => !v)}
                    className="muulti-button cancel-button"
                    style={{ marginTop: 8 }}
                >
                    {removeFeatureImage ? 'Keep current feature image' : 'Remove feature image'}
                </button>
            )}
            {!!displayImageUrl && (
                <img
                    src={displayImageUrl}
                    alt="Feature preview"
                    style={{ width: '100%', marginTop: 8, borderRadius: 8, display: 'block' }}
                />
            )}

            <div className="form-buttons" style={{ marginTop: 16 }}>
                <button onClick={handleSave} disabled={uploading || (!videoFile && !existingVideoUrl)} className="muulti-button">
                    {uploading ? 'Uploading…' : 'Save Panel'}
                </button>

                {enablePublishButton && (
                    <button
                        onClick={handleSaveAndPublish}
                        disabled={uploading || (!videoFile && !existingVideoUrl)}
                        className="muulti-button"
                    >
                        {uploading ? 'Uploading…' : 'Save Panel & Publish'}
                    </button>
                )}

                <button onClick={onCancel} className="muulti-button cancel-button">Cancel</button>
            </div>

            {(localError || error) && <p className="error">{localError || error}</p>}
        </div>
    );
}
